/**
 * AshleyChatWidget · v2.7.65+E103
 * ══════════════════════════════════════════════════════════════════
 * Floating admin chat launcher backed by /api/admin/ashley/*.
 *
 * v2.7.65+E103 upgrades:
 *   • Streaming replies · POST /api/admin/ashley/chat/stream (SSE)
 *   • Voice UI Phase 2 · Whisper STT + OpenAI TTS + animated Orb
 *     with IDLE · LISTENING · PROCESSING · SPEAKING states.
 *
 * Widget shape (collapsed → round pill · expanded → 380×620 panel).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import * as audioManager from "@/lib/audioManager";
import { useAdmin } from "@/contexts/AdminContext";
import {
  MessageCircle, X, Send, Loader2, Minus, Sparkles,
  Mic, MicOff, Volume2, VolumeX, Ear, EarOff,
  History, Database, Globe, Server, ExternalLink,
  ChevronRight, ChevronLeft,
} from "lucide-react";

// v2.7.65+E156 · Rebranded to "Admin Support BOT" with the official
// Buller Taxi crest as the chat avatar. Display strings below flow to
// all visible UI. Function names + variable names (Ashley*) are kept
// as internal identifiers so imports, sessionStorage keys, backend
// endpoints (`/api/admin/ashley/*`), telemetry, presence signals,
// wake-word listeners, TTS pipeline, KB citations, session history —
// everything already wired — continue to work exactly as before.
const DISPLAY_NAME    = "Admin Support BOT";
const AVATAR_IMG_URL  = "/buller-taxi-crest-avatar.png";

function AshleyAvatar() {
  return (
    <img
      src={AVATAR_IMG_URL}
      alt={DISPLAY_NAME}
      className="h-full w-full object-cover"
      draggable={false}
      loading="lazy"
      data-testid="ashley-avatar-img"
    />
  );
}

const ROLES_ALLOWED = new Set([
  "admin", "supervisor", "dispatcher", "team_leader",
  "general_partner", "operating_partner",
]);

// ─── Orb states drive the animated visualisation in the widget header. ─
// IDLE       → gentle amber pulse (default)
// LISTENING  → rose ring, mic recording in progress
// PROCESSING → blue spinner, awaiting STT or LLM response
// SPEAKING   → emerald wave, TTS playback active
const ORB_STATES = { IDLE: "idle", LISTENING: "listening",
                     PROCESSING: "processing", SPEAKING: "speaking" };

// ─── Wake-word chime ────────────────────────────────────────────────
// Short two-tone beep synthesised via WebAudio so we don't have to
// ship an mp3 file. Rising minor third (A4 → C5) that lands in ~0.2 s —
// long enough to notice, short enough to not interrupt speech. Called
// the moment SpeechRecognition matches "hey ashley" so the operator
// knows the mic is now live before they finish their next sentence.
function _playWakeChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0.14;                // soft — never startling
    master.connect(ctx.destination);
    const notes = [
      { freq: 660, at: 0.00, dur: 0.09 },    // E5 blip
      { freq: 880, at: 0.10, dur: 0.14 },    // A5 blip
    ];
    const now = ctx.currentTime;
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = n.freq;
      // ADSR-ish envelope so the chime doesn't click on start/stop.
      g.gain.setValueAtTime(0, now + n.at);
      g.gain.linearRampToValueAtTime(1, now + n.at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + n.at + n.dur);
      osc.connect(g); g.connect(master);
      osc.start(now + n.at);
      osc.stop(now + n.at + n.dur + 0.02);
    }
    // Close the context ~0.4 s later so we don't leak a live audio graph
    // every wake trigger.
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 500);
  } catch { /* audio unavailable — silent fail is fine */ }
}

// Badge → { icon, label, class } mapping keyed by backend source_type.
const SOURCE_LABELS = {
  local_knowledge_base: {
    Icon:  Database,
    label: "Local KB · zero cost",
    cls:   "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
  },
  cache: {
    Icon:  Server,
    label: "Cached · zero cost",
    cls:   "bg-blue-500/10 border-blue-500/40 text-blue-300",
  },
  live_web_search: {
    Icon:  Globe,
    label: "Live web · 1 credit",
    cls:   "bg-amber-500/10 border-amber-500/40 text-amber-300",
  },
  // static_llm gets no badge — quiet default.
};

export default function AshleyChatWidget() {
  const { admin } = useAdmin();
  const roleAllowed = admin && ROLES_ALLOWED.has(String(admin.role || "").toLowerCase());

  const [open, setOpen] = useState(false);
  // v2.7.65+E117 · Ashley hidden state · slides the entire widget off
  // to the right so vital admin controls (Send buttons, dispatch
  // hooks, etc.) are never obstructed. Persisted per browser.
  const [hidden, setHidden] = useState(() => {
    try { return window.localStorage.getItem("ashley_hidden") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("ashley_hidden", hidden ? "1" : "0"); }
    catch { /* quota */ }
    // Emit a body class so the AdminLayout can adjust its grid padding
    // (opt-in — safe when the class isn't styled).
    try {
      document.body.classList.toggle("ashley-visible", !hidden);
    } catch { /* noop */ }
  }, [hidden]);

  // v2.7.65+E122 · Snap-corner preference — ops can pin Ashley to any of
  // the four screen corners. Persisted per browser via localStorage.
  // Values: "br" (default) | "bl" | "tr" | "tl".
  const [corner, setCorner] = useState(() => {
    try {
      const c = window.localStorage.getItem("ashley_corner");
      return ["br", "bl", "tr", "tl"].includes(c) ? c : "br";
    } catch { return "br"; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("ashley_corner", corner); }
    catch { /* quota */ }
  }, [corner]);
  // Derive positioning classes from the current corner.
  const cornerRootClass = {
    br: "fixed bottom-4 right-4",
    bl: "fixed bottom-4 left-4",
    tr: "fixed top-4 right-4",
    tl: "fixed top-4 left-4",
  }[corner] || "fixed bottom-4 right-4";
  // When Ashley is on the RIGHT edge, the hide chevron lives on the LEFT
  // side of the widget (pointing toward the screen edge). Mirror for LEFT
  // corners. Same rule for the vertical restore tab when hidden.
  const rightEdge = corner === "br" || corner === "tr";
  const hideBtnPosClass = rightEdge
    ? "absolute -left-9 top-1/2 -translate-y-1/2 rounded-l-lg border-r-0"
    : "absolute -right-9 top-1/2 -translate-y-1/2 rounded-r-lg border-l-0";
  const hideBtnIcon = rightEdge ? ChevronRight : ChevronLeft;
  const HideBtnIcon = hideBtnIcon;
  const restoreTabPosClass = rightEdge
    ? "fixed top-1/3 right-0 rounded-l-lg border-r-0"
    : "fixed top-1/3 left-0 rounded-r-lg border-l-0";
  const RestoreTabIcon = rightEdge ? ChevronLeft : ChevronRight;

  // v2.7.65+E121 · Alt+A toggles Ashley visible/hidden from anywhere
  // in the admin console so ops can reclaim workspace instantly.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onKey = (e) => {
      if (e.altKey && (e.key === "a" || e.key === "A")) {
        const inField = e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName);
        if (inField) return;
        e.preventDefault();
        setHidden((h) => !h);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [status, setStatus] = useState({ online: false, status_label: "Checking…" });
  // Persistent session_id across page reloads so history rehydrates.
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("bt_ashley_session_id") || ""; }
    catch { return ""; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [greetingMeta, setGreetingMeta] = useState(null);
  // Voice state
  const [orbState, setOrbState] = useState(ORB_STATES.IDLE);
  const [voiceEnabled, setVoiceEnabled] = useState(false);   // TTS on/off toggle
  const [recording, setRecording] = useState(false);
  // Wake word ("Hey Ashley") state — only visible when the browser
  // supports webkitSpeechRecognition (Chrome / Edge · not Safari or FF).
  const [wakeWordSupported] = useState(() => {
    if (typeof window === "undefined") return false;
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  });
  const [wakeWordOn, setWakeWordOn] = useState(false);
  // Past-conversations dropdown (Session Picker · v2.7.65+E105)
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  const [pastSessions, setPastSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const wakeRef = useRef(null);   // SpeechRecognition instance
  // v2.7.65+E156 · Streaming perf — same architecture as the customer
  // Buller Taxi Support widget. See KatieChatWidget for the design note.
  const sendingRef  = useRef(false);
  const bufferRef   = useRef("");
  const flushRafRef = useRef(null);
  const abortRef    = useRef(null);
  const streamIdxRef = useRef(-1);
  useEffect(() => () => {
    if (flushRafRef.current) cancelAnimationFrame(flushRafRef.current);
    if (abortRef.current)    { try { abortRef.current.abort(); } catch { /* noop */ } }
  }, []);

  // Persist session_id whenever it changes so a page reload can reload
  // the transcript from Mongo.
  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem("bt_ashley_session_id", sessionId);
    } catch { /* private-mode */ }
  }, [sessionId]);

  const pollStatus = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/ashley/status");
      setStatus(data);
    } catch (_e) {
      setStatus({ online: false, status_label: "Offline" });
    }
  }, []);

  useEffect(() => {
    if (!roleAllowed) return;
    pollStatus();
    const t = setInterval(pollStatus, 20_000);
    return () => clearInterval(t);
  }, [roleAllowed, pollStatus]);

  useEffect(() => {
    if (!roleAllowed) return;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/ashley/greeting");
        setGreetingMeta(data);
      } catch (_e) { /* silent */ }
    })();
  }, [roleAllowed]);

  // On first widget open, rehydrate persisted transcript from Mongo if
  // we already have a sessionId. Falls back to the deterministic opener
  // when there's no history yet.
  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      let restored = [];
      if (sessionId) {
        try {
          const { data } = await adminApi.get(
            `/admin/ashley/history?session_id=${encodeURIComponent(sessionId)}`,
          );
          restored = Array.isArray(data?.messages) ? data.messages : [];
        } catch (_e) { /* silent — treat as fresh */ }
      }
      const opener = greetingMeta?.greeting
        || `Hi — I'm the ${DISPLAY_NAME}. Ask me about active trips, revenue, drivers, deployment status, or search our dispatch wiki. How can I help?`;
      if (restored.length > 0) {
        setMessages(restored.map((m) => ({
          role: m.role, content: m.content, at: m.at,
        })));
      } else {
        setMessages([{
          role:    "assistant",
          content: opener,
          at:      new Date().toISOString(),
        }]);
      }
      setTimeout(() => inputRef.current?.focus(), 150);
    })();
  }, [open, messages.length, greetingMeta, sessionId]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, busy]);

  // ─── TTS playback ────────────────────────────────────────────────────
  const stopTts = useCallback(() => {
    if (currentAudioRef.current) {
      try { audioManager.untrackEl(currentAudioRef.current); } catch (_e) { /* noop */ }
      try { currentAudioRef.current.pause(); } catch (_e) { /* noop */ }
      currentAudioRef.current.src = "";
      currentAudioRef.current = null;
    }
    setOrbState((s) => (s === ORB_STATES.SPEAKING ? ORB_STATES.IDLE : s));
  }, []);

  const speak = useCallback(async (text) => {
    if (!voiceEnabled || !text || !text.trim()) return;
    stopTts();
    try {
      setOrbState(ORB_STATES.SPEAKING);
      const { data } = await adminApi.post(
        "/admin/ashley/voice/tts",
        { text: text.slice(0, 4000), voice: "shimmer", model: "tts-1", speed: 1.05 },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      currentAudioRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (currentAudioRef.current === audio) currentAudioRef.current = null;
        setOrbState(ORB_STATES.IDLE);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setOrbState(ORB_STATES.IDLE);
      };
      await audio.play();
      // v2.7.65+E219 · Govern Ashley's voice like all system audio (30s cap,
      // counted by the master indicator, killed by the master stop). onStop
      // resets the orb + ref when the cap or master-stop silences it.
      audioManager.track(audio, {
        kind: "standard",
        onStop: () => {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
          if (currentAudioRef.current === audio) currentAudioRef.current = null;
          setOrbState(ORB_STATES.IDLE);
        },
      });
    } catch (_e) {
      setOrbState(ORB_STATES.IDLE);
    }
  }, [voiceEnabled, stopTts]);

  // ─── Streaming send ─────────────────────────────────────────────────
  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text) return;
    // v2.7.65+E156 · Synchronous double-submit guard (React state is async).
    if (sendingRef.current || busy) return;
    sendingRef.current = true;
    stopTts();
    const userMsg = { role: "user", content: text, at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setBusy(true);
    setOrbState(ORB_STATES.PROCESSING);

    // Placeholder assistant bubble — updated in place as tokens arrive.
    const assistantIndex = { current: -1 };
    setMessages((m) => {
      assistantIndex.current = m.length;
      streamIdxRef.current   = m.length;
      return [...m, { role: "assistant", content: "", at: new Date().toISOString(), streaming: true }];
    });

    // v2.7.65+E156 · rAF-throttled token flusher (~60fps ceiling).
    bufferRef.current = "";
    const scheduleFlush = () => {
      if (flushRafRef.current) return;
      flushRafRef.current = requestAnimationFrame(() => {
        flushRafRef.current = null;
        const idx = streamIdxRef.current;
        if (idx < 0) return;
        const next = bufferRef.current;
        setMessages((m) => {
          if (!m[idx]) return m;
          if (m[idx].content === next) return m;
          const copy = m.slice();
          copy[idx] = { ...copy[idx], content: next };
          return copy;
        });
      });
    };

    if (abortRef.current) { try { abortRef.current.abort(); } catch { /* noop */ } }
    const ac = new AbortController();
    abortRef.current = ac;

    let finalText = "";
    try {
      const token = localStorage.getItem("rtg_admin_token");
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const resp = await fetch(`${base}/api/admin/ashley/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Accept":        "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, session_id: sessionId || undefined }),
        signal: ac.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(`stream failed · ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split SSE frames on double-newline.
        let sep;
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const lines = frame.split("\n").filter(Boolean);
          const eventLine = lines.find((l) => l.startsWith("event:"));
          const dataLine  = lines.find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const event = (eventLine || "event: message").slice(6).trim();
          const raw   = dataLine.slice(5).trim();
          let parsed;
          try { parsed = JSON.parse(raw); } catch { parsed = raw; }
          if (event === "meta" && parsed?.session_id) {
            setSessionId(parsed.session_id);
            // Tag the in-flight assistant bubble with source_type +
            // sources as soon as they arrive so the badge + citations
            // render alongside the streaming tokens.
            if (parsed?.source_type || parsed?.sources) {
              setMessages((m) => {
                const copy = m.slice();
                const idx = assistantIndex.current;
                if (copy[idx]) {
                  copy[idx] = {
                    ...copy[idx],
                    source_type: parsed.source_type || copy[idx].source_type,
                    sources:     parsed.sources     || copy[idx].sources,
                  };
                }
                return copy;
              });
            }
          } else if (event === "token" && typeof parsed === "string") {
            finalText += parsed;
            bufferRef.current = finalText;
            scheduleFlush();
          } else if (event === "done") {
            if (flushRafRef.current) { cancelAnimationFrame(flushRafRef.current); flushRafRef.current = null; }
            setMessages((m) => {
              const copy = m.slice();
              const idx = assistantIndex.current;
              if (copy[idx]) copy[idx] = {
                ...copy[idx],
                content:     bufferRef.current || copy[idx].content,
                streaming:   false,
                kb_hits:     parsed?.kb_hits,
                source_type: parsed?.source_type || copy[idx].source_type,
                sources:     parsed?.sources     || copy[idx].sources,
              };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        setMessages((m) => {
          const copy = m.slice();
          const idx = assistantIndex.current;
          const errText = `Sorry — I couldn't reach the server. ${e?.message || e}`;
          if (copy[idx]) copy[idx] = { role: "assistant", content: errText, at: new Date().toISOString(), error: true };
          return copy;
        });
      }
      finalText = "";
    } finally {
      if (flushRafRef.current) { cancelAnimationFrame(flushRafRef.current); flushRafRef.current = null; }
      streamIdxRef.current = -1;
      setBusy(false);
      sendingRef.current = false;
      setTimeout(() => inputRef.current?.focus(), 100);
      if (voiceEnabled && finalText) {
        // TTS the assistant reply after streaming completes.
        speak(finalText);
      } else {
        setOrbState(ORB_STATES.IDLE);
      }
    }
  }, [input, busy, sessionId, stopTts, voiceEnabled, speak]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = async () => {
    try {
      const { data } = await adminApi.post("/admin/ashley/session/new");
      const newSid = data?.session_id || "";
      setSessionId(newSid);
      try {
        if (newSid) localStorage.setItem("bt_ashley_session_id", newSid);
        else localStorage.removeItem("bt_ashley_session_id");
      } catch { /* private-mode */ }
      setMessages([]);
      stopTts();
      setOrbState(ORB_STATES.IDLE);
    } catch (_e) { /* silent */ }
  };

  // ─── Past-conversations picker (v2.7.65+E105) ───────────────────────
  const loadPastSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const { data } = await adminApi.get("/admin/ashley/sessions/mine?limit=15");
      setPastSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (_e) {
      setPastSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const openSessionMenu = useCallback(() => {
    setSessionMenuOpen(true);
    loadPastSessions();
  }, [loadPastSessions]);

  const resumeSession = useCallback(async (sid) => {
    if (!sid) return;
    setSessionMenuOpen(false);
    stopTts();
    setSessionId(sid);
    try { localStorage.setItem("bt_ashley_session_id", sid); }
    catch { /* private-mode */ }
    // Immediately rehydrate the transcript from Mongo.
    try {
      const { data } = await adminApi.get(
        `/admin/ashley/history?session_id=${encodeURIComponent(sid)}`,
      );
      const restored = Array.isArray(data?.messages) ? data.messages : [];
      setMessages(restored.map((m) => ({
        role: m.role, content: m.content, at: m.at,
      })));
    } catch (_e) {
      setMessages([]);
    }
  }, [stopTts]);

  // ─── v2.7.65+E164 · Manually-triggered web search ─────────────────
  // Called ONLY when the dispatcher clicks the cyan "Web" button.
  // Never fires automatically. Renders a clearly-labeled "WEB RESEARCH"
  // bubble with the answer, impact hint, source links, and the
  // checked-at timestamp. Cannot mutate any trip / driver / integration.
  const handleWebSearch = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, {
      role: "user", content: text, at: new Date().toISOString(),
      web_search_query: true,
    }]);
    const placeholderIdx = { current: -1 };
    setMessages((m) => {
      placeholderIdx.current = m.length;
      return [...m, {
        role: "assistant", content: "Searching the public web…",
        at: new Date().toISOString(),
        web_research: true, streaming: true,
      }];
    });
    try {
      const { data } = await adminApi.post("/admin/ashley/web-search", {
        query: text, max_results: 6,
      });
      setMessages((m) => {
        const copy = m.slice();
        const idx = placeholderIdx.current;
        if (copy[idx]) copy[idx] = {
          ...copy[idx],
          streaming: false,
          web_research: true,
          ok: !!data?.ok,
          content: data?.ok
            ? (data.answer || "See the linked sources below.")
            : (data?.reason || "The web search couldn't be completed."),
          web_sources: data?.sources || [],
          web_checked_at: data?.checked_at,
          web_provider:   data?.provider,
          web_impact:     data?.impact_hint,
          web_query:      data?.query || text,
        };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = m.slice();
        const idx = placeholderIdx.current;
        if (copy[idx]) copy[idx] = {
          role: "assistant", web_research: true, ok: false,
          content: `The web search couldn't be completed — ${e?.response?.data?.detail || e?.message || "network error"}.`,
          at: new Date().toISOString(),
        };
        return copy;
      });
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, busy]);

  // ─── Voice · MediaRecorder → Whisper ─────────────────────────────────
  const startRecording = useCallback(async () => {
    if (recording || busy) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Mic access isn't supported in this browser.",
        at: new Date().toISOString(), error: true,
      }]);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) {
          setOrbState(ORB_STATES.IDLE);
          return;
        }
        setOrbState(ORB_STATES.PROCESSING);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "clip.webm");
          fd.append("language", "en");
          const { data } = await adminApi.post("/admin/ashley/voice/stt", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (text) send(text);
          else setOrbState(ORB_STATES.IDLE);
        } catch (_e) {
          setOrbState(ORB_STATES.IDLE);
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
      setOrbState(ORB_STATES.LISTENING);
    } catch (_e) {
      setOrbState(ORB_STATES.IDLE);
    }
  }, [recording, busy, send]);

  const stopRecording = useCallback(() => {
    setRecording(false);
    try { mediaRecorderRef.current?.stop(); } catch (_e) { /* noop */ }
  }, []);

  // ─── Wake word · "Hey Ashley" ────────────────────────────────────────
  // Uses the Web Speech API (webkit/standard SpeechRecognition) to
  // continuously listen for the phrase "hey ashley" (or "hi ashley").
  // On detection, opens the widget if closed and triggers the same
  // MediaRecorder path used by the push-to-talk mic button.
  //
  // Only mounted when the browser supports SpeechRecognition (Chrome +
  // Edge). Safari / Firefox get no toggle and this effect is a no-op.
  useEffect(() => {
    if (!wakeWordSupported || !wakeWordOn) {
      if (wakeRef.current) {
        try { wakeRef.current.stop(); } catch (_e) { /* noop */ }
        wakeRef.current = null;
      }
      return undefined;
    }
    const Impl = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Impl();
    rec.continuous     = true;
    rec.interimResults = true;
    rec.lang           = "en-US";
    // Regex looks for common misfires ("ashley", "ash lee", "hey ashley").
    // Keep permissive — worst case is we spuriously trigger a mic listen
    // which the operator can cancel by releasing.
    const WAKE_RE = /\b(hey|hi)\s+(ashley|ash\s*lee|ashleigh)\b/i;
    rec.onresult = (evt) => {
      // Concatenate every result fragment we haven't consumed yet.
      let heard = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        heard += evt.results[i][0]?.transcript || "";
      }
      if (WAKE_RE.test(heard)) {
        _playWakeChime();
        setOpen(true);
        if (!recording && !busy) {
          // Kick off a mic listen turn. Slight delay so the SpeechRecog
          // audio stream releases the mic before MediaRecorder grabs it.
          setTimeout(() => { startRecording(); }, 200);
        }
      }
    };
    rec.onerror = () => { /* transient · onend will restart */ };
    // Auto-restart when the browser stops (some tabs kill the stream
    // after 60 s idle). Guard with the current toggle.
    rec.onend = () => {
      if (wakeWordOn && wakeRef.current === rec) {
        try { rec.start(); } catch (_e) { /* already started */ }
      }
    };
    wakeRef.current = rec;
    try { rec.start(); } catch (_e) { /* already running */ }
    return () => {
      try { rec.stop(); } catch (_e) { /* noop */ }
      if (wakeRef.current === rec) wakeRef.current = null;
    };
  }, [wakeWordOn, wakeWordSupported, recording, busy, startRecording]);

  // Cleanup on unmount
  useEffect(() => () => stopTts(), [stopTts]);

  const statusColor = status.online ? "#22c55e" : "#71717a";
  const statusLabel = status.status_label || (status.online ? "Active" : "Offline");

  if (!roleAllowed) return null;

  // v2.7.65+E117 · When fully hidden, only a slim pinned tab is visible
  // so the admin workspace grid gets the full width back. Clicking the
  // tab restores the launcher pill (which the operator can then click
  // to expand the panel as before).
  if (hidden) {
    return (
      <button
        type="button"
        onClick={() => setHidden(false)}
        data-testid="ashley-restore-tab"
        className={`${restoreTabPosClass} z-[65] bg-amber-500/90 hover:bg-amber-500 border border-amber-300/60 text-zinc-950 px-2 py-4 shadow-xl flex flex-col items-center gap-1.5 group transition-all`}
        title={`Show ${DISPLAY_NAME} · Alt+A`}
        aria-label={`Show ${DISPLAY_NAME} (Alt+A)`}
      >
        <RestoreTabIcon className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform"/>
        <span className="text-[10px] uppercase tracking-widest font-black [writing-mode:vertical-rl]">{DISPLAY_NAME}</span>
        <span className="text-[8px] font-bold opacity-60 [writing-mode:vertical-rl]">Alt+A</span>
      </button>
    );
  }

  return (
    <div
      className={`${cornerRootClass} z-[70] font-sans`}
      data-testid="ashley-widget-root"
      data-corner={corner}
      aria-live="polite"
    >
      {/* v2.7.65+E117 · Hide-away chevron — one-tap slide off-screen so
          the operator can hit Send buttons or dispatch controls beneath.
          v2.7.65+E122 · Position mirrors when Ashley snaps to a LEFT corner. */}
      <button
        type="button"
        onClick={() => setHidden(true)}
        data-testid="ashley-hide-btn"
        className={`${hideBtnPosClass} h-11 w-7 bg-amber-500/90 hover:bg-amber-500 border border-amber-300/60 text-zinc-950 hover:text-zinc-950 flex items-center justify-center shadow-lg transition-colors`}
        title={`Hide ${DISPLAY_NAME} · Alt+A · reclaim the workspace`}
        aria-label={`Hide ${DISPLAY_NAME} (Alt+A)`}
      >
        <HideBtnIcon className="h-5 w-5"/>
      </button>

      {/* v2.7.65+E122 · Snap-corner picker — 4 tiny dots below the widget
          so ops can pin Ashley wherever they like. The active corner is
          filled amber; the rest are outlines. */}
      <div
        className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1"
        data-testid="ashley-corner-picker"
        role="group"
        aria-label={`${DISPLAY_NAME} position`}
      >
        {[
          ["tl", "Top-left"],
          ["tr", "Top-right"],
          ["bl", "Bottom-left"],
          ["br", "Bottom-right"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setCorner(k)}
            data-testid={`ashley-corner-${k}`}
            title={`Snap ${DISPLAY_NAME} to ${label}`}
            aria-label={`Snap ${DISPLAY_NAME} to ${label}`}
            aria-pressed={corner === k}
            className={`h-3 w-3 rounded-sm border transition-all ${
              corner === k
                ? "bg-amber-400 border-amber-300 shadow-[0_0_6px_rgba(251,191,36,0.6)]"
                : "bg-zinc-900/70 border-zinc-600 hover:border-amber-400"
            }`}
          />
        ))}
      </div>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ashley-launcher"
          className="group flex items-center gap-2 rounded-full pl-1 pr-4 py-1 bg-zinc-900 border border-amber-400/30 shadow-[0_10px_30px_rgba(0,0,0,0.45)] hover:border-amber-400/70 hover:shadow-[0_16px_40px_rgba(251,191,36,0.25)] transition-all"
          aria-label={`Open ${DISPLAY_NAME}`}
        >
          <div className="relative">
            <div className="h-10 w-10 rounded-full overflow-hidden border-2 border-amber-400/50 group-hover:border-amber-400 transition-colors">
              <AshleyAvatar/>
            </div>
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-900 animate-pulse"
              style={{ backgroundColor: statusColor }}
              data-testid="ashley-launcher-dot"
              title={statusLabel}
            />
          </div>
          <div className="text-left leading-tight">
            <div className="text-xs font-bold text-amber-300 uppercase tracking-widest">{DISPLAY_NAME}</div>
            <div className="text-[10px] text-zinc-400">Your AI Assistant</div>
          </div>
          <MessageCircle className="h-4 w-4 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1"/>
        </button>
      ) : (
        <AshleyPanel
          onClose={() => setOpen(false)}
          onReset={reset}
          statusColor={statusColor}
          statusLabel={statusLabel}
          messages={messages}
          input={input}
          setInput={setInput}
          onSend={send}
          onKey={onKey}
          busy={busy}
          listRef={listRef}
          inputRef={inputRef}
          version={status.version}
          levelLabel={greetingMeta?.level_label}
          orbState={orbState}
          voiceEnabled={voiceEnabled}
          setVoiceEnabled={setVoiceEnabled}
          recording={recording}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          onStopTts={stopTts}
          wakeWordSupported={wakeWordSupported}
          wakeWordOn={wakeWordOn}
          setWakeWordOn={setWakeWordOn}
          sessionMenuOpen={sessionMenuOpen}
          onOpenSessionMenu={openSessionMenu}
          onCloseSessionMenu={() => setSessionMenuOpen(false)}
          pastSessions={pastSessions}
          sessionsLoading={sessionsLoading}
          activeSessionId={sessionId}
          onResumeSession={resumeSession}
          onWebSearch={handleWebSearch}
        />
      )}
    </div>
  );
}

// ─── Expanded panel ────────────────────────────────────────────────────
function AshleyPanel({
  onClose, onReset, statusColor, statusLabel,
  messages, input, setInput, onSend, onKey, busy,
  listRef, inputRef, version, levelLabel,
  orbState, voiceEnabled, setVoiceEnabled,
  recording, onStartRecording, onStopRecording, onStopTts,
  wakeWordSupported, wakeWordOn, setWakeWordOn,
  sessionMenuOpen, onOpenSessionMenu, onCloseSessionMenu,
  pastSessions, sessionsLoading, activeSessionId, onResumeSession,
  onWebSearch,
}) {
  const toggleVoice = () => {
    if (voiceEnabled) onStopTts();
    setVoiceEnabled((v) => !v);
  };
  return (
    <div
      className="relative w-[min(400px,calc(100vw-2rem))] h-[min(620px,calc(100vh-6rem))] bg-zinc-950 border border-zinc-800 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.55)] flex flex-col overflow-hidden"
      data-testid="ashley-panel"
      role="dialog"
      aria-label={`${DISPLAY_NAME} AI Assistant`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-amber-500/10 via-zinc-900 to-zinc-900">
        <div className="relative">
          <AshleyOrb state={orbState}>
            <AshleyAvatar/>
          </AshleyOrb>
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-zinc-900 animate-pulse"
            style={{ backgroundColor: statusColor }}
            data-testid="ashley-panel-dot"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-zinc-100 flex items-center gap-1.5">
            {DISPLAY_NAME}
            <Sparkles className="h-3 w-3 text-amber-400"/>
          </div>
          <div className="text-[10px] flex items-center gap-1.5 font-semibold uppercase tracking-widest"
               style={{ color: statusColor }}
               data-testid="ashley-status-label">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusColor }}/>
            {statusLabel}
            {levelLabel && (
              <span className="ml-1 text-[9px] text-zinc-500 normal-case tracking-normal" data-testid="ashley-level-label">
                · {levelLabel}
              </span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={toggleVoice}
          data-testid="ashley-voice-toggle"
          data-voice-on={voiceEnabled ? "1" : "0"}
          title={voiceEnabled ? `Mute ${DISPLAY_NAME} (turn off text-to-speech)` : `Unmute ${DISPLAY_NAME} (read replies aloud)`}
          className={`p-1 ${voiceEnabled ? "text-emerald-400 hover:text-emerald-300" : "text-zinc-500 hover:text-zinc-200"}`}
          aria-label={voiceEnabled ? `Mute ${DISPLAY_NAME}` : `Unmute ${DISPLAY_NAME}`}
        >
          {voiceEnabled ? <Volume2 className="h-4 w-4"/> : <VolumeX className="h-4 w-4"/>}
        </button>

        {wakeWordSupported && (
          <button
            type="button"
            onClick={() => setWakeWordOn((v) => !v)}
            data-testid="ashley-wake-toggle"
            data-wake-on={wakeWordOn ? "1" : "0"}
            title={wakeWordOn
              ? "Wake word ON — say “Hey Ashley” to start a voice turn hands-free"
              : "Wake word OFF — click to enable “Hey Ashley” hotword"}
            className={`p-1 ${wakeWordOn ? "text-blue-400 hover:text-blue-300" : "text-zinc-500 hover:text-zinc-200"}`}
            aria-label={wakeWordOn ? "Disable wake word" : "Enable wake word"}
          >
            {wakeWordOn ? <Ear className="h-4 w-4"/> : <EarOff className="h-4 w-4"/>}
          </button>
        )}

        <button
          type="button"
          onClick={onOpenSessionMenu}
          data-testid="ashley-history-toggle"
          title="Past conversations · resume a previous thread"
          className="text-zinc-500 hover:text-amber-400 p-1 relative"
          aria-label="Past conversations"
        >
          <History className="h-4 w-4"/>
        </button>

        <button
          type="button"
          onClick={onReset}
          data-testid="ashley-reset"
          title="Start a fresh conversation"
          className="text-zinc-500 hover:text-amber-400 p-1"
          aria-label="Reset conversation"
        >
          <RotateIcon/>
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="ashley-minimize"
          title="Minimize"
          className="text-zinc-500 hover:text-zinc-100 p-1"
          aria-label="Minimize Ashley"
        >
          <Minus className="h-4 w-4"/>
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="ashley-close"
          title="Close"
          className="text-zinc-500 hover:text-rose-400 p-1"
          aria-label="Close Ashley"
        >
          <X className="h-4 w-4"/>
        </button>
      </div>

      {/* Past-conversations dropdown · session picker */}
      {sessionMenuOpen && (
        <SessionPicker
          sessions={pastSessions}
          loading={sessionsLoading}
          activeSessionId={activeSessionId}
          onResume={onResumeSession}
          onClose={onCloseSessionMenu}
        />
      )}

      {/* Messages */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-zinc-950"
        data-testid="ashley-message-list"
      >
        {messages.map((m, i) => (
          <MessageBubble key={i} msg={m}/>
        ))}
        {busy && !messages.some((m) => m.streaming) && (
          <div className="flex items-center gap-2 text-xs text-zinc-500 italic">
              <Loader2 className="h-3 w-3 animate-spin"/>{DISPLAY_NAME} is thinking…
          </div>
        )}
      </div>

      {/* Input row */}
      <div className="border-t border-zinc-800 bg-zinc-900 px-3 py-2">
        <div className="flex items-end gap-2">
          <button
            type="button"
            onMouseDown={onStartRecording}
            onMouseUp={onStopRecording}
            onMouseLeave={recording ? onStopRecording : undefined}
            onTouchStart={onStartRecording}
            onTouchEnd={onStopRecording}
            disabled={busy}
            data-testid="ashley-mic"
            data-recording={recording ? "1" : "0"}
            title={recording ? "Release to send" : "Hold to talk"}
            className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-40 ${
              recording
                ? "bg-rose-500 text-zinc-950 animate-pulse"
                : "bg-zinc-950 border border-zinc-700 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
            }`}
            aria-label="Push to talk"
          >
            {recording ? <MicOff className="h-4 w-4"/> : <Mic className="h-4 w-4"/>}
          </button>
          <textarea
            ref={inputRef}
            data-testid="ashley-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder={recording ? "Listening…" : "Ask about trips, drivers, revenue, wiki…"}
            className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-amber-400 resize-none max-h-32"
            style={{ minHeight: "38px" }}
          />
          <button
            type="button"
            onClick={() => onSend()}
            disabled={busy || !input.trim()}
            data-testid="ashley-send"
            className="h-9 px-3 rounded-lg bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
            aria-label="Send message"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Send className="h-3.5 w-3.5"/>}
          </button>
          {/* v2.7.65+E164 · Manually-triggered web search — only fires
              when the dispatcher clicks. Result is rendered as a
              "WEB RESEARCH" card with sources + checked-at. Never
              automatic, never on the customer chat. */}
          <button
            type="button"
            onClick={() => onWebSearch && onWebSearch()}
            disabled={busy || !input.trim() || !onWebSearch}
            data-testid="ashley-web-search"
            className="h-9 px-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/50 text-yellow-200 font-bold text-[10px] uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
            aria-label="Search the web (manual)"
            title="Search the public web — manual trigger only. Never runs automatically."
          >
            <Globe className="h-3.5 w-3.5"/><span className="hidden sm:inline">Web</span>
          </button>
        </div>
      </div>

      <div className="text-center text-[10px] text-zinc-600 py-1.5 border-t border-zinc-800 bg-zinc-950"
           data-testid="ashley-footer">
        Powered by Bison AI Chat{version ? ` · ${version}` : ""}
        <span className="mx-1 text-zinc-700">·</span>
        <span data-testid="ashley-orb-state">Orb: {orbState}</span>
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const isStreaming = msg.streaming && !isUser;
  const badge = !isUser && msg.source_type ? SOURCE_LABELS[msg.source_type] : null;
  const sources = Array.isArray(msg.sources) ? msg.sources : [];
  // v2.7.65+E164 · Web-research bubble variant — visually distinct from
  // internal / KB / live-data replies. Explicitly labeled "WEB RESEARCH"
  // with the checked-at timestamp and source links.
  const isWeb = !isUser && msg.web_research;
  const webSources = Array.isArray(msg.web_sources) ? msg.web_sources : [];
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
          isUser
            ? "bg-amber-400 text-zinc-950 rounded-br-sm"
            : msg.error
              ? "bg-rose-950/40 border border-rose-700 text-rose-200 rounded-bl-sm"
              : isWeb
                ? "bg-yellow-950/40 border border-yellow-500/50 text-yellow-50 rounded-bl-sm"
                : "bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-bl-sm"
        }`}
        data-testid={isUser ? "ashley-msg-user" : (isWeb ? "ashley-msg-web-research" : "ashley-msg-assistant")}
        data-streaming={isStreaming ? "1" : "0"}
        data-source-type={msg.source_type || ""}
        data-web-research={isWeb ? "1" : "0"}
      >
        {isWeb && (
          <div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-yellow-500/30">
            <Globe className="h-3 w-3 text-yellow-300"/>
            <span className="text-[9px] uppercase tracking-widest font-black text-yellow-200" data-testid="ashley-web-research-label">
              Web Research
            </span>
            {msg.ok === false && (
              <span className="text-[9px] uppercase tracking-widest font-bold text-rose-300 border border-rose-500/40 px-1 py-0.5 rounded bg-rose-500/10">
                Not verified
              </span>
            )}
            {msg.web_checked_at && (
              <span
                className="text-[9px] text-yellow-400/80 ml-auto"
                data-testid="ashley-web-checked-at"
                title="Timestamp the public web was checked"
              >
                {new Date(msg.web_checked_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
              </span>
            )}
          </div>
        )}
        {msg.content}
        {isStreaming && !msg.content && (
          <span className="inline-flex items-center gap-1.5 text-amber-300/90 text-[13px] italic"
                data-testid="ashley-checking">
            <Loader2 className="h-3.5 w-3.5 animate-spin"/>
            {DISPLAY_NAME} is checking…
          </span>
        )}
        {isStreaming && msg.content && (
          <span className="inline-block w-1.5 h-3 ml-0.5 bg-amber-400 align-middle animate-pulse"
                data-testid="ashley-stream-caret"/>
        )}
        {isWeb && msg.web_impact && (
          <div className="mt-2 pt-2 border-t border-yellow-500/30 text-[11px] text-yellow-200/90" data-testid="ashley-web-impact">
            <span className="font-bold uppercase tracking-widest text-yellow-300 text-[9px]">Dispatch impact · </span>
            {msg.web_impact}
          </div>
        )}
        {isWeb && webSources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-yellow-500/30">
            <div className="text-[9px] uppercase tracking-widest font-bold text-yellow-300 mb-1.5">
              Sources · {webSources.length}
            </div>
            <div className="space-y-1.5" data-testid="ashley-web-sources">
              {webSources.slice(0, 8).map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-zinc-950/60 hover:bg-zinc-950 border border-yellow-500/20 rounded px-2 py-1.5"
                  data-testid={`ashley-web-source-${i}`}
                >
                  <div className="text-[11px] font-bold text-yellow-100 truncate">{s.title}</div>
                  {s.snippet && <div className="text-[10px] text-zinc-400 line-clamp-2">{s.snippet}</div>}
                  <div className="text-[9px] text-yellow-400/60 truncate mt-0.5">{s.url}</div>
                </a>
              ))}
            </div>
            <div className="text-[9px] text-yellow-400/60 mt-1.5 italic">
              Information only — web research cannot create, edit, cancel, or dispatch trips.
            </div>
          </div>
        )}
        {/* Source-type badge */}
        {badge && (
          <div className="mt-2">
            <span
              data-testid="ashley-source-badge"
              data-source-type={msg.source_type}
              className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${badge.cls}`}
            >
              <badge.Icon className="h-2.5 w-2.5"/>
              {badge.label}
            </span>
          </div>
        )}
        {/* Citation chips */}
        {sources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-zinc-800/60">
            <div className="text-[9px] uppercase tracking-widest font-bold text-zinc-500 mb-1">
              Referenced
            </div>
            <div className="flex flex-wrap gap-1"
                 data-testid="ashley-citations">
              {sources.map((s, i) => {
                const isExternal = /^https?:\/\//.test(s.url || "");
                const Cite = isExternal ? "a" : "a";
                return (
                  <Cite
                    key={i}
                    href={s.url || "#"}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="inline-flex items-center gap-1 text-[10px] text-blue-300 bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 px-1.5 py-0.5 rounded transition"
                    data-testid={`ashley-citation-${i}`}
                  >
                    {(s.name || "source").slice(0, 60)}
                    <ExternalLink className="h-2.5 w-2.5"/>
                  </Cite>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RotateIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/>
      <path d="M3 3v5h5"/>
    </svg>
  );
}

// ─── Animated Orb ──────────────────────────────────────────────────────
// Visualises the current voice-UI state around Ashley's avatar. Colours
// are matched to state (amber idle · rose listening · blue processing ·
// emerald speaking). All motion is CSS-only so we avoid a Framer/Motion
// dependency for a single tiny widget.
function AshleyOrb({ state, children }) {
  const ring = useMemo(() => {
    switch (state) {
      case ORB_STATES.LISTENING:
        return { border: "border-rose-500", shadow: "0 0 0 4px rgba(244,63,94,0.35)",  anim: "orb-listen" };
      case ORB_STATES.PROCESSING:
        return { border: "border-blue-400", shadow: "0 0 0 4px rgba(96,165,250,0.35)", anim: "orb-spin" };
      case ORB_STATES.SPEAKING:
        return { border: "border-emerald-400", shadow: "0 0 0 4px rgba(52,211,153,0.35)", anim: "orb-speak" };
      default:
        return { border: "border-amber-400/70", shadow: "0 0 0 2px rgba(251,191,36,0.20)", anim: "" };
    }
  }, [state]);
  return (
    <>
      <style>{`
        @keyframes ashleyOrbListen  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }
        @keyframes ashleyOrbSpeak   { 0%,100% { transform: scale(1); } 25% { transform: scale(1.05); } 75% { transform: scale(0.97); } }
        @keyframes ashleyOrbSpinRot { to { transform: rotate(360deg); } }
        .orb-listen   { animation: ashleyOrbListen 0.9s ease-in-out infinite; }
        .orb-speak    { animation: ashleyOrbSpeak  0.6s ease-in-out infinite; }
        .orb-spin::before {
          content: ""; position: absolute; inset: -3px; border-radius: 9999px;
          border: 2px solid transparent; border-top-color: rgb(96,165,250);
          animation: ashleyOrbSpinRot 0.9s linear infinite;
        }
      `}</style>
      <div
        className={`relative h-11 w-11 rounded-full overflow-hidden border-2 ${ring.border} ${ring.anim}`}
        style={{ boxShadow: ring.shadow }}
        data-testid="ashley-orb"
        data-orb-state={state}
      >
        {children}
      </div>
    </>
  );
}


// ─── Past-conversations dropdown ───────────────────────────────────────
// Absolutely positioned inside the AshleyPanel — sits between the header
// and the messages so it doesn't clip. Selecting a row calls
// `onResume(sid)` which rehydrates the transcript from Mongo via
// `/history?session_id=…`.
function SessionPicker({ sessions, loading, activeSessionId, onResume, onClose }) {
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMs = now - d;
      const mins = Math.floor(diffMs / 60_000);
      if (mins < 1)   return "just now";
      if (mins < 60)  return `${mins}m ago`;
      if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
      return d.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch { return iso.slice(0, 10); }
  };
  return (
    <div
      className="absolute inset-x-0 top-[64px] z-30 mx-3 rounded-xl border border-zinc-700 bg-zinc-950 shadow-2xl overflow-hidden"
      data-testid="ashley-session-picker"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 bg-zinc-900">
        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300 flex items-center gap-1.5">
          <History className="h-3 w-3"/>Past Conversations
        </div>
        <button type="button" onClick={onClose}
                data-testid="ashley-session-picker-close"
                className="text-zinc-500 hover:text-zinc-200 p-1">
          <X className="h-3.5 w-3.5"/>
        </button>
      </div>
      <div className="max-h-[240px] overflow-y-auto">
        {loading ? (
          <div className="py-6 text-center text-xs text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin"/>Loading…
          </div>
        ) : sessions.length === 0 ? (
          <div className="py-6 text-center text-xs text-zinc-500 italic"
               data-testid="ashley-session-picker-empty">
            No past conversations yet.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {sessions.map((s) => {
              const isActive = s.session_id === activeSessionId;
              return (
                <li key={s.session_id}>
                  <button
                    type="button"
                    onClick={() => onResume(s.session_id)}
                    disabled={isActive}
                    data-testid={`ashley-session-row-${s.session_id}`}
                    className={`w-full text-left px-3 py-2 text-xs transition ${
                      isActive
                        ? "bg-amber-500/10 text-amber-200 cursor-default"
                        : "hover:bg-zinc-900 text-zinc-300"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono truncate text-[10px]">
                        {s.session_id.slice(0, 8)}…{s.session_id.slice(-4)}
                      </span>
                      <span className="text-[10px] text-zinc-500">
                        {fmt(s.last_seen)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-zinc-500">
                      <span>{s.turn_count || 0} turn{(s.turn_count || 0) === 1 ? "" : "s"}</span>
                      {isActive && (
                        <span className="ml-1 text-amber-400 font-bold uppercase tracking-widest">
                          · current
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
